import { useRef, useEffect, useCallback, useState } from "react"
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import { navigate } from "vike/client/router"
import Button from "aldoo-ra/Button"
import MenuGroup from "aldoo-ra/MenuGroup"
import {
  GlobeAltIcon,
  LockClosedIcon,
  EyeIcon,
  NoSymbolIcon,
  PauseCircleIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  TrashIcon,
  KeyIcon,
  ChevronRightIcon,
  ShieldCheckIcon,
  ClockIcon,
  ChartBarIcon,
  ArrowUpTrayIcon,
  PencilSquareIcon,
  CloudArrowUpIcon,
  PlayCircleIcon,
  SwatchIcon,
  CheckIcon as Check,
  InboxArrowDownIcon,
  EnvelopeIcon,
  BellAlertIcon,
} from "@heroicons/react/24/outline"
import { GetCMSContent } from "aldoo-ra/CMS/api"
import { url as cmsUrl } from "aldoo-ra/CMS/url"

// ─── Small helpers ────────────────────────────────────────────────────────────

function SectionBadge({ children }) {
  return (
    <span className="inline-block w-fit rounded-full bg-primary/15 border border-primary/40 text-primary text-xs font-semibold uppercase tracking-wide px-3 py-1 mb-4">
      {children}
    </span>
  )
}

function CheckRow({ children }) {
  return (
    <li className="flex items-center gap-3">
      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 rounded-full bg-accent_1/10">
        <Check className="w-3 h-3 text-accent_1" strokeWidth={3} />
      </span>

      <Typography variant="body2_dim" className="text-left my-0">
        {children}
      </Typography>
    </li>
  )
}

// 3D tilt wrapper (ported from aldoo-meet)
function TiltWrapper({ children, className = "", maxX = 12, maxY = 16 }) {
  const containerRef = useRef(null)
  const rafRef = useRef(null)
  const cur = useRef({ x: 0, y: 0 })
  const tgt = useRef({ x: 0, y: 0 })
  const divRef = useRef(null)

  const animate = useCallback(() => {
    const lerp = 0.08
    cur.current.x += (tgt.current.x - cur.current.x) * lerp
    cur.current.y += (tgt.current.y - cur.current.y) * lerp
    if (divRef.current) {
      const { x, y } = cur.current
      divRef.current.style.transform = `rotateY(${y}deg) rotateX(${x}deg) rotateZ(${y * 0.08}deg)`
    }
    rafRef.current = requestAnimationFrame(animate)
  }, [])

  function start() { if (!rafRef.current) rafRef.current = requestAnimationFrame(animate) }
  function stop() {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null }
    tgt.current = { x: 1, y: -4 }
    rafRef.current = requestAnimationFrame(animate)
    setTimeout(() => { if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null } }, 1200)
  }
  function move(e) {
    const r = containerRef.current?.getBoundingClientRect()
    if (!r) return
    const dx = (e.clientX - (r.left + r.width / 2)) / (r.width / 2)
    const dy = (e.clientY - (r.top + r.height / 2)) / (r.height / 2)
    tgt.current = { x: -dy * maxX, y: dx * maxY }
  }
  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }, [])

  return (
    <div ref={containerRef} className={className} style={{ perspective: "1200px" }}
      onMouseEnter={start} onMouseMove={move} onMouseLeave={stop}>
      <div ref={divRef} style={{ transform: "rotateY(-5deg) rotateX(2deg)", transformStyle: "preserve-3d", willChange: "transform" }}>
        {children}
      </div>
    </div>
  )
}

// ─── Mockups for the 4 main sections ───────────────────────────────────────────

function Shell({ title, accent = "#3b82f6", headerIcon: HeaderIcon, children }) {
  return (
    <div className="relative w-full max-w-[560px] mx-auto select-none">
      <div className="absolute -top-10 -left-10 w-72 h-72 rounded-full blur-3xl pointer-events-none"
        style={{ background: `${accent}26` }} />
      <div className="relative rounded-2xl border border-white/10"
        style={{ background: "#0d1117", boxShadow: "0 32px 64px -12px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06)" }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.07] rounded-t-2xl"
          style={{ background: "rgba(255,255,255,0.03)" }}>
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
          </div>
          <span className="flex items-center gap-2 text-gray-200 text-sm font-medium tracking-tight">
            {HeaderIcon && <HeaderIcon className="w-4 h-4" style={{ color: accent }} />}
            {title}
          </span>
          <div className="w-8" />
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  )
}

// Data-driven mockup: renders the section's actual bullets as feature rows,
// each with its own accent icon. Sized to fill the text column.
function FeatureMockup({ title, accent, headerIcon, rows }) {
  return (
    <Shell title={title} accent={accent} headerIcon={headerIcon}>
      <div className="flex flex-col gap-3">
        {rows.map(({ Icon, label }, i) => (
          <div key={i} className="flex items-center gap-4 rounded-xl border border-white/10 px-4 py-4"
            style={{ background: "rgba(255,255,255,0.03)" }}>
            <span className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: `${accent}1f` }}>
              <Icon className="w-5 h-5" style={{ color: accent }} />
            </span>
            <span className="text-sm text-gray-200 leading-snug flex-1">{label}</span>
            <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: accent }}>
              <Check className="w-3 h-3 text-white" strokeWidth={3} />
            </span>
          </div>
        ))}
      </div>
    </Shell>
  )
}

// ─── Themes showcase mockup ─────────────────────────────────────────────────
// Renders 3 sample share backgrounds (pulled from the share_backgrounds CMS
// gallery — same pool the public share landing uses for its default rotation)
// arranged as a tilted stack of theme cards. The accent colour is read by the
// outer MainSection chrome.
function ThemesShowcase({ accent }) {
  const [images, setImages] = useState([])
  useEffect(() => {
    let alive = true
    GetCMSContent({ match: { 'content.type': 'ImageGallery', 'content.id': 'share_backgrounds' } })
      .then((res) => {
        const refs = res?.data?.[0]?.content?.images
        if (!alive || !Array.isArray(refs) || !refs.length) return
        // Pick 3 distinct random refs each mount — fresh look on every visit.
        const pool = [...refs]
        const picks = []
        const n = Math.min(3, pool.length)
        for (let i = 0; i < n; i++) {
          const idx = Math.floor(Math.random() * pool.length)
          picks.push(pool.splice(idx, 1)[0])
        }
        setImages(picks.map((r) => cmsUrl(r)))
      })
      .catch(() => {})
    return () => { alive = false }
  }, [])

  // Stable tilt offsets so the stack reads as 3 distinct cards.
  const TILTS = [
    { rotate: -8, x: -32, y: 16, z: 1 },
    { rotate: 0,  x: 0,   y: 0,  z: 3 },
    { rotate: 8,  x: 32,  y: 16, z: 2 },
  ]

  return (
    // Fixed pixel size — TiltWrapper's inner div has auto width, so a
    // descendant with only absolute children collapses to ~0×0 when its size
    // is derived from aspect-ratio. Explicit dimensions keep the stack visible.
    <div className="relative w-[min(520px,90vw)] h-[360px] mx-auto">
      {(images.length ? images : [null, null, null]).map((src, i) => {
        const t = TILTS[i] || TILTS[1]
        return (
          <div
            key={i}
            className="absolute top-1/2 left-1/2 w-[68%] aspect-video rounded-2xl overflow-hidden border border-white/15 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.55)]"
            style={{
              // Center the card first (-50% offsets), then apply per-card
              // tilt + offset. Single composite transform so the offsets
              // don't fight the centering.
              transform: `translate(calc(-50% + ${t.x}px), calc(-50% + ${t.y}px)) rotate(${t.rotate}deg)`,
              zIndex: t.z,
              background: `linear-gradient(135deg, ${accent}40, #0b1220 80%)`,
            }}
          >
            {src ? (
              <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover" />
            ) : null}
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/55 to-transparent" />
            {/* Mini "Download" pill — previews the theme primary colour. */}
            <div className="absolute left-3 right-3 bottom-3 flex items-center justify-between">
              <span className="text-[11px] font-semibold text-white/95 truncate pr-2">transfer.aldoo.com</span>
              <span
                className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white shadow-sm"
                style={{ background: accent }}
              >
                Download
              </span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─── Main alternating section ───────────────────────────────────────────────────

function MainSection({ badge, title, description, bullets, mockup, reverse }) {
  return (
    <section className="relative py-16 md:py-24 overflow-hidden">
      <div className={`relative z-10 w-full max-w-6xl mx-auto px-6 flex flex-col-reverse ${reverse ? "lg:flex-row-reverse" : "lg:flex-row"} items-center gap-12 lg:gap-16`}>
        <div className="flex-1 w-full flex justify-center">
          <TiltWrapper className="w-full flex justify-center">
            {mockup}
          </TiltWrapper>
        </div>

        <div className="w-full lg:w-[45%] flex flex-col items-center lg:items-start text-center lg:text-left flex-shrink-0">
          <SectionBadge>{badge}</SectionBadge>

          <Typography  
            variant="h2" 
            className="text-text mt-0 mb-4"
          >
            {title}
          </Typography>

          <Typography variant="body1_dim">
            {description}
          </Typography>

          <ul className="space-y-3 mb-0">
            {bullets.map((b, i) => <CheckRow key={i}>{b}</CheckRow>)}
          </ul>
        </div>
      </div>
    </section>
  )
}

// ─── Feature cards carousel ──────────────────────────────────────────────────────

function FeaturesCarousel({ cards }) {
  const scrollRef = useRef(null)
  const cardRefs = useRef([])
  const dragRef = useRef({ dragging: false, startX: 0, scrollLeft: 0 })
  const autoScrollRef = useRef(null)
  const tripled = [...cards, ...cards, ...cards]
  const count = cards.length

  function updateScales() {
    const el = scrollRef.current
    if (!el) return
    const centerX = el.scrollLeft + el.offsetWidth / 2
    const halfWidth = el.offsetWidth / 2
    cardRefs.current.forEach((card) => {
      if (!card) return
      const cardCenter = card.offsetLeft + card.offsetWidth / 2
      const distance = Math.abs(cardCenter - centerX)
      const scale = 1 - 0.5 * Math.min(1, distance / halfWidth)
      card.style.transform = `scale(${scale})`
    })
  }

  function startAutoScroll() {
    if (autoScrollRef.current) return
    let accum = 0
    function tick() {
      const el = scrollRef.current
      if (!el) return
      accum += 0.15
      if (accum >= 1) {
        el.scrollLeft += Math.floor(accum)
        accum -= Math.floor(accum)
        updateScales()
      }
      autoScrollRef.current = requestAnimationFrame(tick)
    }
    autoScrollRef.current = requestAnimationFrame(tick)
  }

  function stopAutoScroll() {
    if (autoScrollRef.current) { cancelAnimationFrame(autoScrollRef.current); autoScrollRef.current = null }
  }

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    let oneSet = 0
    function init() {
      oneSet = el.scrollWidth / 3
      el.scrollLeft = oneSet
      updateScales()
      startAutoScroll()
    }
    function onScroll() {
      if (!oneSet) return
      if (el.scrollLeft > oneSet * 1.9) el.scrollLeft -= oneSet
      else if (el.scrollLeft < oneSet * 0.1) el.scrollLeft += oneSet
      updateScales()
    }
    requestAnimationFrame(init)
    el.addEventListener("scroll", onScroll, { passive: true })
    el.addEventListener("wheel", stopAutoScroll, { passive: true })
    el.addEventListener("touchstart", stopAutoScroll, { passive: true })
    return () => {
      el.removeEventListener("scroll", onScroll)
      el.removeEventListener("wheel", stopAutoScroll)
      el.removeEventListener("touchstart", stopAutoScroll)
      stopAutoScroll()
    }
  }, [count])

  function onMouseDown(e) {
    const el = scrollRef.current
    stopAutoScroll()
    if (dragRef.current.rafId) cancelAnimationFrame(dragRef.current.rafId)
    dragRef.current = { dragging: true, startX: e.pageX - el.offsetLeft, scrollLeft: el.scrollLeft, lastX: e.pageX, velocity: 0, rafId: null }
    el.style.cursor = "grabbing"
    el.style.userSelect = "none"
  }
  function onMouseMove(e) {
    if (!dragRef.current.dragging) return
    e.preventDefault()
    const el = scrollRef.current
    const x = e.pageX - el.offsetLeft
    dragRef.current.velocity = e.pageX - dragRef.current.lastX
    dragRef.current.lastX = e.pageX
    el.scrollLeft = dragRef.current.scrollLeft - (x - dragRef.current.startX)
  }
  function onMouseUp() {
    if (!dragRef.current.dragging) return
    dragRef.current.dragging = false
    const el = scrollRef.current
    if (el) { el.style.cursor = "grab"; el.style.userSelect = "" }
    let velocity = -dragRef.current.velocity
    function momentum() {
      if (!el || Math.abs(velocity) < 0.5) return
      el.scrollLeft += velocity
      velocity *= 0.94
      updateScales()
      dragRef.current.rafId = requestAnimationFrame(momentum)
    }
    dragRef.current.rafId = requestAnimationFrame(momentum)
  }

  return (
    <div className="relative z-10 w-full">
      <div className="absolute left-0 top-0 bottom-0 w-20 lg:w-72 z-20 pointer-events-none"
        style={{ background: "linear-gradient(to right, rgb(var(--color-canvas)) 30%, transparent 100%)" }} />
      <div className="absolute right-0 top-0 bottom-0 w-20 lg:w-72 z-20 pointer-events-none"
        style={{ background: "linear-gradient(to left, rgb(var(--color-canvas)) 30%, transparent 100%)" }} />
      <div
        ref={scrollRef}
        style={{ overflowX: "auto", overflowY: "visible", scrollbarWidth: "none", msOverflowStyle: "none", cursor: "grab" }}
        onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
      >
        <div className="flex gap-3 md:gap-4 lg:gap-5 px-6 lg:px-12 pt-10 pb-6 md:pb-16 lg:pb-[100px]" style={{ width: "max-content" }}>
          {tripled.map((card, i) => {
            const Icon = card.icon
            return (
              <div key={i} ref={(el) => { cardRefs.current[i] = el }} className="flex-none w-[280px] md:w-[320px]" style={{ willChange: "transform" }}>
                <TiltWrapper maxX={8} maxY={10}>
                  <div className="relative" style={{ transformStyle: "preserve-3d" }}>
                    <div className="feature-card-shadow absolute inset-0 rounded-2xl pointer-events-none"
                      style={{ "--card-sc": `${card.color}59`, transform: "translateZ(-1px)" }} />
                    <div
                      className="group relative min-h-[260px] md:min-h-[320px] flex flex-col gap-6 bg-paper border border-bordered/50 rounded-2xl p-8 overflow-hidden"
                      onMouseMove={(e) => {
                        const r = e.currentTarget.getBoundingClientRect()
                        e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`)
                        e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`)
                      }}
                    >
                      <div className="absolute rounded-full scale-0 group-hover:scale-100 transition-transform duration-500 ease-in-out pointer-events-none z-0"
                        style={{ backgroundColor: card.color, width: "800px", height: "800px", left: "-348px", top: "-348px" }} />
                      <div className="absolute inset-0 pointer-events-none z-[5] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        style={{ background: "radial-gradient(circle 180px at var(--mx, 50%) var(--my, 50%), rgba(255,255,255,0.22) 0%, transparent 100%)" }} />
                      <div className="relative z-10 w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: card.color }}>
                        <Icon className="w-7 h-7 text-white mx-auto" />
                      </div>
                      <Typography variant="h6" className="relative z-10 text-text mb-2 mt-0 transition-colors duration-300 group-hover:text-white">
                        {card.title}
                      </Typography>
                      <Typography variant="body2_dim" className="block relative z-10 leading-relaxed transition-colors duration-300 group-hover:text-white/90 mt-auto">
                        {card.description}
                      </Typography>
                    </div>
                  </div>
                </TiltWrapper>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function FeaturesPage() {
  const texts = TextBundle({ id: "transfer_site" })

  const sections = [
    {
      accent: "#06b6d4",
      headerIcon: CloudArrowUpIcon,
      rowIcons: [ArrowPathIcon, PauseCircleIcon, PlayCircleIcon],
      badge: texts?.sResumeBadge,
      title: texts?.sResumeTitle,
      description: texts?.sResumeDesc,
      bullets: [
        texts?.sResumeB1,
        texts?.sResumeB2,
        texts?.sResumeB3,
      ],
    },
    {
      accent: "#22c55e",
      headerIcon: InboxArrowDownIcon,
      rowIcons: [InboxArrowDownIcon, EnvelopeIcon, BellAlertIcon],
      badge: texts?.sReceiveBadge,
      title: texts?.sReceiveTitle,
      description: texts?.sReceiveDesc,
      bullets: [
        texts?.sReceiveB1,
        texts?.sReceiveB2,
        texts?.sReceiveB3,
      ],
    },
    {
      accent: "#ec4899",
      headerIcon: SwatchIcon,
      rowIcons: [SwatchIcon, PencilSquareIcon, EyeIcon],
      badge: texts?.sThemesBadge,
      title: texts?.sThemesTitle,
      description: texts?.sThemesDesc,
      bullets: [
        texts?.sThemesB1,
        texts?.sThemesB2,
        texts?.sThemesB3,
      ],
      // Custom mockup — gallery of sample share backgrounds.
      customMockup: ThemesShowcase,
    },
    {
      accent: "#3b82f6",
      headerIcon: GlobeAltIcon,
      rowIcons: [GlobeAltIcon, LockClosedIcon, ArrowUpTrayIcon],
      badge: texts?.s1Badge,
      title: texts?.s1Title,
      description: texts?.s1Desc,
      bullets: [texts?.s1b1, texts?.s1b2, texts?.s1b3],
    },
    {
      accent: "#8b5cf6",
      headerIcon: ShieldCheckIcon,
      rowIcons: [PauseCircleIcon, NoSymbolIcon, KeyIcon],
      badge: texts?.s2Badge,
      title: texts?.s2Title,
      description: texts?.s2Desc,
      bullets: [texts?.s2b1, texts?.s2b2, texts?.s2b3],
    },
    {
      accent: "#10b981",
      headerIcon: ChartBarIcon,
      rowIcons: [EyeIcon, ChartBarIcon, DocumentTextIcon],
      badge: texts?.s3Badge,
      title: texts?.s3Title,
      description: texts?.s3Desc,
      bullets: [texts?.s3b1, texts?.s3b2, texts?.s3b3],
    },
    {
      accent: "#f59e0b",
      headerIcon: ClockIcon,
      rowIcons: [ClockIcon, ArrowPathIcon, ArrowPathIcon],
      badge: texts?.s4Badge,
      title: texts?.s4Title,
      description: texts?.s4Desc,
      bullets: [texts?.s4b1, texts?.s4b2, texts?.s4b3],
    },
  ]

  const cards = [
    { icon: GlobeAltIcon, color: "#3b82f6", title: texts?.publicLinks, description: texts?.publicLinksDesc },
    { icon: LockClosedIcon, color: "#8b5cf6", title: texts?.authAccess, description: texts?.authAccessDesc },
    { icon: EyeIcon, color: "#10b981", title: texts?.viewDownloadStats, description: texts?.viewDownloadStatsDesc },
    { icon: NoSymbolIcon, color: "#ef4444", title: texts?.downloadLimit, description: texts?.downloadLimitDesc },
    { icon: PauseCircleIcon, color: "#f59e0b", title: texts?.pauseResume, description: texts?.pauseResumeDesc },
    { icon: ArrowPathIcon, color: "#06b6d4", title: texts?.extendRenew, description: texts?.extendRenewDesc },
    { icon: DocumentTextIcon, color: "#ec4899", title: texts?.notesRecipients, description: texts?.notesRecipientsDesc },
    { icon: TrashIcon, color: "#64748b", title: texts?.deleteAnytime, description: texts?.deleteAnytimeDesc },
    { icon: KeyIcon, color: "#a855f7", title: texts?.passwordProtection, description: texts?.passwordProtectionDesc },
    { icon: PencilSquareIcon, color: "#0ea5e9", title: texts?.liveEditing, description: texts?.liveEditingDesc },
  ]

  return (
    <>
      <style>{`
        .feature-card-shadow { box-shadow: 0 12px 28px -5px var(--card-sc), 0 4px 12px -3px rgba(0,0,0,0.07); }
        @media (min-width: 768px) { .feature-card-shadow { box-shadow: 0 30px 60px -10px var(--card-sc), 0 10px 30px -5px rgba(0,0,0,0.10); } }
      `}</style>

      <div className="relative overflow-x-clip bg-canvas min-h-screen">
        {/* Hero + MainSections share one continuous gradient background */}
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-secondary/15 to-secondary/30 pointer-events-none" />
          <section className="relative -mt-16 pt-28 md:pt-36 text-center px-6">
            <div className="relative z-10 max-w-3xl mx-auto flex flex-col items-center">
              <SectionBadge>
                {texts?.featuresBadge}
              </SectionBadge>

              <Typography 
                component="h1" 
                variant="h1" 
                className="text-text mt-0 mb-5"
              >
                {texts?.everythingYouCanDo}
              </Typography>

              <Typography 
                variant="body1" 
                className="text-text_secondary mb-8 lg:mb-10"
              >
                {texts?.heroDesc}
              </Typography>

              <Button 
                variant="primary" 
                onClick={() => navigate("/")}
              >
                {texts?.createFileShare}
              </Button>
            </div>

            <div className="relative z-10 max-w-[500px] flex flex-col items-center justify-center gap-0 mt-20 mx-auto">
              <Typography 
                variant="body1" 
                className="text-text_secondary my-0"
              >
                {texts?.downloadApp}
              </Typography>

              <MenuGroup
              renderAs={"hlist"}
              name="StoreButtons"
              itemsAsLinks={false}
              styleOverrides={{
                root_item: "py-0",
                menu_column: "hidden",
                menu_item: "px-0 my-0"
              }}
              
              menuItemRender={(item) => {
                const url = item.link?.[0]?.url
                return (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="w-[140px] md:w-[170px]" style={{ aspectRatio: "220/65" }}>
                      <img
                        src={item.image}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </a>
                )
              }}
            />
            </div>
          </section>

          {/* Main alternating sections */}
          {sections.map((s, i) => (
            <MainSection
              key={i}
              reverse={i % 2 === 1}
              badge={s.badge}
              title={s.title}
              description={s.description}
              bullets={s.bullets}
              mockup={
                s.customMockup
                  ? <s.customMockup accent={s.accent} />
                  : (
                    <FeatureMockup
                      title={s.title}
                      accent={s.accent}
                      headerIcon={s.headerIcon}
                      rows={s.bullets
                        .filter(Boolean)
                        .map((label, j) => ({ Icon: s.rowIcons[j] || s.headerIcon, label }))}
                    />
                  )
              }
            />
          ))}
        </div>

        {/* Carousel */}
        <section className="relative pb-8" style={{ overflowX: "clip" }}>
          <Typography component="h2" variant="h2" className="text-text text-center mx-8 mt-16 mb-2">
            {texts?.everyFeatureGlance}
          </Typography>
          <div className="text-center mb-2">
            <span className="inline-flex items-center gap-1 text-text_secondary text-sm">
              <ChevronRightIcon className="w-4 h-4" />
              {texts?.swipeDrag}
            </span>
          </div>
          <FeaturesCarousel cards={cards} />
        </section>
      </div>
    </>
  )
}
